import React, { useState, useEffect } from 'react';
import { WorkflowProvider, useWorkflow } from './context/WorkflowContext';
import Header from './components/Header';
import LandingPage from './components/LandingPage';
import SearchPage from './components/SearchPage';
import EntityResolutionPage from './components/EntityResolutionPage';
import ReportPage from './components/ReportPage';
import SettingsPage from './components/SettingsPage';
import MethodologyPage from './components/MethodologyPage';
import LegalNoticePage from './components/LegalNoticePage';
import SavedReportsPage from './components/SavedReportsPage';
import AuthPage from './components/AuthPage';
import { motion, AnimatePresence } from 'framer-motion';
import { getSession, signOut } from './api';

const WorkflowOrchestrator = ({ user, onSignOut, onRequireAuth }) => {
  const { phase, setPhase } = useWorkflow();

  return (
    <div className="min-h-screen bg-sonar-bg text-slate-300 font-sans flex flex-col selection:bg-sonar-green selection:text-black">
      <Header user={user} onSignOut={onSignOut} isPublic={!user} onSignIn={onRequireAuth} />
      
      <main className="flex-1 w-full relative">
        {/* Persistent Home Link on all sub-pages */}
        {phase !== 0 && (
          <button 
            onClick={() => setPhase(0)}
            className="fixed bottom-6 right-6 z-50 bg-sonar-card border border-sonar-border text-sonar-green px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest hover:border-sonar-green transition-all shadow-2xl backdrop-blur-md"
          >
            Return Home
          </button>
        )}

        <AnimatePresence mode="wait">
          <motion.div
            key={phase}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
          >
            {phase === 0 && <LandingPage user={user} onLaunch={() => user ? setPhase(1) : onRequireAuth()} />}
            {phase === 1 && <SearchPage />}
            {phase === 2 && <EntityResolutionPage user={user} />}
            {phase === 3 && <ReportPage />}
            {phase === 5 && <SettingsPage />}
            {phase === 6 && <MethodologyPage />}
            {phase === 7 && <LegalNoticePage />}
            {phase === 8 && <SavedReportsPage user={user} />}
          </motion.div>
        </AnimatePresence>
      </main>

      <footer className="bg-[#030a0d] border-t border-sonar-border py-12 mt-auto">
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row justify-between items-center gap-8">
          <div>
            <div className="flex items-center gap-2 mb-4" onClick={() => setPhase(0)} style={{ cursor: 'pointer' }}>
              <div className="w-6 h-6 rounded-full border border-sonar-green flex items-center justify-center text-sonar-green text-[10px] font-bold">S</div>
              <span className="font-bold text-white tracking-widest text-sm uppercase">Credit<span className="text-sonar-green">Sonar</span></span>
            </div>
            <p className="text-xs text-slate-600 font-mono">© 2024 CreditSonar Intelligence Systems. All rights reserved.</p>
            <p className="text-[10px] text-slate-700 font-mono mt-1">
              Outputs are provided "as-is" and do not constitute regulated financial advice.
            </p>
          </div>
          <div className="flex gap-8 flex-wrap justify-center">
            <button onClick={() => setPhase(0)} className="text-xs text-slate-500 hover:text-sonar-green font-mono uppercase tracking-widest">Platform</button>
            <button onClick={() => setPhase(6)} className="text-xs text-slate-500 hover:text-sonar-green font-mono uppercase tracking-widest">Methodology</button>
            <button onClick={() => setPhase(7)} className="text-xs text-slate-500 hover:text-amber-400 font-mono uppercase tracking-widest">Legal</button>
          </div>
        </div>
      </footer>
    </div>
  );
};

const AppContent = () => {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [showAuth, setShowAuth] = useState(false);
  const { setPhase } = useWorkflow();

  useEffect(() => {
    const session = getSession();
    if (session) setUser(session);
    setChecking(false);
  }, []);

  const handleAuth = (userData) => {
    setUser(userData);
    setShowAuth(false);
    setPhase(1);
  };

  const handleSignOut = () => {
    signOut();
    setUser(null);
    setPhase(0);
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-sonar-bg flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-sonar-green border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user && showAuth) {
    return <AuthPage onAuth={handleAuth} onBack={() => setShowAuth(false)} />;
  }

  return (
    <WorkflowOrchestrator 
      user={user} 
      onSignOut={handleSignOut} 
      onRequireAuth={() => setShowAuth(true)} 
    />
  );
};

const App = () => {
  return (
    <WorkflowProvider>
      <AppContent />
    </WorkflowProvider>
  );
};

export default App;